import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Power, PowerOff } from 'lucide-react';
import { useState } from 'react';

import {
  getCupomCategoriasControllerListarQueryKey,
  useCupomCategoriasControllerCriar,
  useCupomCategoriasControllerDefinirAtivo,
  useCupomCategoriasControllerListar,
} from '@/api/generated/admin-cupom-categorias/admin-cupom-categorias';
import { getCuponsControllerListarQueryKey } from '@/api/generated/admin-cupons/admin-cupons';
import { ApiError } from '@/api/http-client';
import { EstadoCarregando, EstadoErro } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * Categorias de cupom.
 *
 * ⚠️ Não há excluir: `Cupom.categoriaId` é `SetNull`, então apagar soltaria os
 * cupons da categoria sem erro nenhum — e eles sumiriam do agrupamento da
 * vitrine em silêncio. Desativar preserva o vínculo.
 */
export function CategoriasDialogo({ aberto, aoFechar }: Props) {
  const { pode } = usePermissoes();
  const queryClient = useQueryClient();

  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useCupomCategoriasControllerListar({
    query: { enabled: aberto },
  });

  const criar = useCupomCategoriasControllerCriar();
  const definirAtivo = useCupomCategoriasControllerDefinirAtivo();

  const podeCriar = pode('admin_cupons.criar');
  const podeEditar = pode('admin_cupons.editar');

  async function recarregar() {
    await queryClient.invalidateQueries({
      queryKey: getCupomCategoriasControllerListarQueryKey(),
    });
    // A listagem de cupons mostra o nome da categoria em cada linha.
    await queryClient.invalidateQueries({ queryKey: getCuponsControllerListarQueryKey() });
  }

  async function adicionar() {
    setErro(null);
    try {
      await criar.mutateAsync({ data: { nome: nome.trim() } });
      await recarregar();
      setNome('');
      toast.success('Categoria criada.');
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível criar a categoria.');
    }
  }

  async function alternar(id: string, nomeDaCategoria: string, ativo: boolean) {
    try {
      await definirAtivo.mutateAsync({ categoriaId: id, data: { ativo } });
      await recarregar();
      toast.success(ativo ? `"${nomeDaCategoria}" reativada.` : `"${nomeDaCategoria}" desativada.`);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível alterar a categoria.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Categorias de cupom</DialogTitle>
          <DialogDescription>
            Agrupam os cupons na vitrine do Clube. Desativar não desvincula os cupons.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <EstadoCarregando rotulo="Carregando categorias..." />
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : (
          <div className="space-y-4">
            {erro && (
              <Alert variant="destructive">
                <AlertDescription>{erro}</AlertDescription>
              </Alert>
            )}

            {podeCriar && (
              <div className="flex gap-2">
                <Input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Nome da categoria"
                  aria-label="Nome da nova categoria"
                />
                <Button
                  onClick={() => void adicionar()}
                  disabled={nome.trim().length < 2 || criar.isPending}
                >
                  {criar.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  Adicionar
                </Button>
              </div>
            )}

            {data.itens.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                Nenhuma categoria ainda.
              </p>
            ) : (
              <ul className="divide-y divide-brand/10 max-h-72 overflow-y-auto">
                {data.itens.map((categoria) => (
                  <li key={categoria.id} className="flex items-center justify-between gap-3 py-2">
                    <div>
                      <span className="text-sm text-foreground">{categoria.nome}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {categoria.totalCupons}{' '}
                        {categoria.totalCupons === 1 ? 'cupom' : 'cupons'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {!categoria.ativo && (
                        <Badge variant="outline" className="text-destructive border-destructive/40">
                          Desativada
                        </Badge>
                      )}

                      {podeEditar && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={
                            categoria.ativo
                              ? `Desativar ${categoria.nome}`
                              : `Reativar ${categoria.nome}`
                          }
                          onClick={() =>
                            void alternar(categoria.id, categoria.nome, !categoria.ativo)
                          }
                        >
                          {categoria.ativo ? (
                            <PowerOff className="w-4 h-4 text-destructive" />
                          ) : (
                            <Power className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
